const BUN_API = process.env.REACT_APP_BUN_API || "http://localhost:5001";
const WS_PATH = "/ws/eeg";

// Simple singleton-style service for subscribing to EEG WebSocket data
class EEGService {
  constructor() {
    this.ws = null;
    this.subscribers = new Set();
    this.connectionStateSubscribers = new Set(); // For connection state changes
    this.isConnecting = false;
    this.connectionState = "disconnected"; // "disconnected" | "connecting" | "connected"
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity; // Keep trying to reconnect
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.reconnectTimer = null;
    this.shouldReconnect = true;
  }

  setConnectionState(newState) {
    if (this.connectionState !== newState) {
      this.connectionState = newState;
      // Notify all connection state subscribers
      for (const cb of this.connectionStateSubscribers) {
        try {
          cb(newState);
        } catch (err) {
          console.error("[EEGService] Connection state subscriber error:", err);
        }
      }
    }
  }

  get wsUrl() {
    // Convert http(s):// to ws(s):// for WebSocket URL
    try {
      const url = new URL(BUN_API);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.pathname = WS_PATH;
      return url.toString();
    } catch {
      // Fallback: assume already ws:// or wss://
      return `${BUN_API.replace(/^http/, "ws")}${WS_PATH}`;
    }
  }

  connect() {
    if (this.ws || this.isConnecting) return;
    this.isConnecting = true;
    this.setConnectionState("connecting");

    // Clear any pending reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const url = this.wsUrl;
    // eslint-disable-next-line no-console
    console.log(`[EEGService] Connecting to WebSocket: ${url} (attempt ${this.reconnectAttempts + 1})`);
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000; // Reset delay on successful connection
      this.setConnectionState("connected");
      // eslint-disable-next-line no-console
      console.log("[EEGService] WebSocket connected:", url);
    };

    this.ws.onclose = (event) => {
      // eslint-disable-next-line no-console
      console.log("[EEGService] WebSocket closed", event.code, event.reason);
      this.ws = null;
      this.isConnecting = false;
      this.setConnectionState("disconnected");
      
      // Auto-reconnect if we have subscribers and should reconnect
      if (this.shouldReconnect && this.subscribers.size > 0) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (err) => {
      // eslint-disable-next-line no-console
      console.error("[EEGService] WebSocket error:", err);
      // onclose will handle reconnection
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        // Expecting { type: "eeg" | "snapshot", data: { ... } }
        if (!msg || !msg.data) {
          console.warn("[EEGService] Received message without data:", msg);
          return;
        }
        // eslint-disable-next-line no-console
        console.log("[EEGService] Broadcasting message to", this.subscribers.size, "subscribers, type:", msg.type);
        for (const cb of this.subscribers) {
          try {
            cb(msg);
          } catch (err) {
            console.error("[EEGService] Subscriber error:", err);
          }
        }
      } catch (err) {
        console.error("[EEGService] Failed to parse message:", err);
      }
    };
  }

  scheduleReconnect() {
    if (this.reconnectTimer || !this.shouldReconnect) return;
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // eslint-disable-next-line no-console
      console.error("[EEGService] Max reconnection attempts reached");
      return;
    }

    this.reconnectAttempts++;
    // eslint-disable-next-line no-console
    console.log(`[EEGService] Scheduling reconnect in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts})`);
    this.setConnectionState("connecting");
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.subscribers.size > 0 && !this.ws && !this.isConnecting) {
        this.connect();
      }
    }, this.reconnectDelay);

    // Exponential backoff with max delay
    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
  }

  /**
   * Subscribe to connection state changes.
   * Callback receives the state: "disconnected" | "connecting" | "connected"
   * Returns an unsubscribe function.
   */
  subscribeToConnectionState(callback) {
    this.connectionStateSubscribers.add(callback);
    // Immediately call with current state
    try {
      callback(this.connectionState);
    } catch (err) {
      console.error("[EEGService] Connection state callback error:", err);
    }

    return () => {
      this.connectionStateSubscribers.delete(callback);
    };
  }

  /**
   * Get current connection state.
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Subscribe to EEG messages.
   * Callback receives the full message: { type, data }.
   * Returns an unsubscribe function.
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    this.shouldReconnect = true;

    // Lazily connect when the first subscriber registers
    if (!this.ws && !this.isConnecting) {
      this.connect();
    }

    return () => {
      this.subscribers.delete(callback);
      // Close WS when no subscribers left
      if (this.subscribers.size === 0) {
        this.shouldReconnect = false;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        if (this.ws) {
          this.ws.close();
        }
      }
    };
  }
}

const eegService = new EEGService();

export default eegService;



